// What a page about the project holds (AboutPage.tsx), projected from the
// shell model:
//  - Info: the project's purpose (the brief the assistant reads on every
//    step) and who it is for (StudioInfo.tsx);
//  - Learning objectives: their coverage over the outline's parts; add one,
//    take one off (StudioObjectives.tsx);
//  - Sources: the project's sources, one opens in the middle; Add a source
//    uploads (StudioSources.tsx).

export { StudioAbout };

import { exhaustSwitchCase } from "@bilda/utils";
import type { ReactNode } from "react";
import type { ProjectObjectives } from "../../../app/ports/project.js";
import type { StudioCard } from "../../../components/Studio/studioCards.js";
import {
    type DeskAbout,
    openThingCommands,
} from "../../../components/Studio/studioDesk.js";
import type { OutlineRow } from "../../../components/StudioEditor/studioPages.js";
import { StudioObjectives } from "../../../components/StudioObjectives/StudioObjectives.js";
import { StudioInfo } from "../../../components/StudioPanel/StudioInfo.js";
import {
    type StudioSource,
    StudioSources,
} from "../../../components/StudioPanel/StudioSources.js";
import { useAppShell } from "../context.js";

const StudioAbout = ({
    about,
    cards,
    outline,
    objectives,
}: {
    readonly about: DeskAbout;
    readonly cards: readonly StudioCard[];
    // The outline's rows, unfolded, and the project's objectives (undefined
    // while the first answer is on its way).
    readonly outline: readonly OutlineRow[];
    readonly objectives: ProjectObjectives | undefined;
}): ReactNode => {
    const { library, frame, health, studio, learningContexts, openFilePicker } =
        useAppShell();
    const online = health === "online";
    switch (about) {
        case "info": {
            const project = (library?.projects ?? []).find(
                (p) => p.id === frame.studioProject,
            );
            const audiences = (project?.audiences ?? []).flatMap((id) =>
                learningContexts
                    .filter((lc) => lc.id === id)
                    .map((lc) => lc.title),
            );
            return (
                <StudioInfo
                    key={project?.id}
                    purpose={project?.description ?? ""}
                    audiences={audiences}
                    online={online}
                    onDescribe={studio.describeProject}
                    onClear={studio.clearProjectDescription}
                />
            );
        }
        case "objectives":
            return (
                <StudioObjectives
                    key={frame.studioProject}
                    outline={outline}
                    objectives={objectives}
                    online={online}
                    onAdd={studio.addObjective}
                    onRemove={studio.removeObjective}
                />
            );
        case "sources":
            return (
                <StudioSources
                    sources={cards.flatMap((c): readonly StudioSource[] =>
                        c.type === "source"
                            ? [
                                  {
                                      id: c.id,
                                      title: c.title,
                                      format: c.format,
                                      readStatus: c.readStatus,
                                  },
                              ]
                            : [],
                    )}
                    online={online}
                    onOpen={(id) =>
                        studio.onDesk(...openThingCommands(cards, id))
                    }
                    onAdd={openFilePicker}
                />
            );
        default:
            return exhaustSwitchCase(about);
    }
};
